import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BoltIcon from "@mui/icons-material/Bolt";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import moneyExchangeIcon from "../../Assets/Images/transaction-data.png";
import sampleIcon from "../../Assets/Images/info.png";
import { UserAuthContext } from "../../Contexts/UserAuthContext";
import "./DanceVideoAi.css";

/* =========================================================
   CONFIG
   ========================================================= */

const API_BASE_URL = "https://backend.whalesai.com";
const GENERATE_ENDPOINT = `${API_BASE_URL}/api/v1/wan-video-ai/generate`;

// Raw files are sent directly as multipart/form-data (no separate upload step).
// Change these field names here if the backend expects different ones.
const IMAGE_FIELD_NAME = "imageUrls";
const VIDEO_FIELD_NAME = "videoUrls";

const MAX_IMAGES = 3;
const IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
const VIDEO_TYPES = ["video/mp4", "video/webm"];

const RESOLUTIONS = [
    { value: "720p", label: "720p", tag: "HD" },
    { value: "1080p", label: "1080p", tag: "FHD" },
];

const ASPECTS = [
    { value: "16:9", label: "Widescreen" },
    { value: "9:16", label: "Vertical" },
];

const DEFAULT_PROMPT_EXAMPLE =
    "@Image1 is Person 1, @Image2 is Person 2. In @Video1, replace Person 1 with @Image1 and Person 2 with @Image2.";

const authHeader = () => ({
    authorization: `Bearer ${localStorage.getItem("aiUser")}`,
});

const showAlert = (text, icon = "warning") =>
    swal({ text, icon, buttons: "Ok", className: "modal_class_success" });

/* =========================================================
   COMPONENT
   ========================================================= */

export default function DanceVideoAi() {
    const { AIuser, setUserRefetch } = useContext(UserAuthContext);
    const navigate = useNavigate();

    // Inputs
    const [images, setImages] = useState([]); // [{ id, file, preview }]
    const [imageUrls, setImageUrls] = useState([]);
    const [video, setVideo] = useState(null); // { file, preview }
    const [videoUrl, setVideoUrl] = useState("");
    const [prompt, setPrompt] = useState("");
    const [duration, setDuration] = useState("10");
    const [resolution, setResolution] = useState("1080p");
    const [aspectRatio, setAspectRatio] = useState("16:9");

    // Status
    const [isLoading, setIsLoading] = useState(false);
    const [loadingStep, setLoadingStep] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    // Result
    const [result, setResult] = useState(null); // { transactionId, cost, videoUrl, ... }
    const [copied, setCopied] = useState(false);

    const imageInputRef = useRef(null);
    const videoInputRef = useRef(null);
    const submittingRef = useRef(false);
    const previewsRef = useRef({ images: [], video: null });

    const credits = AIuser?.credits
        ? Math.floor(AIuser.credits).toLocaleString()
        : "0";

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Keep latest previews so unmount cleanup can revoke them
    useEffect(() => {
        previewsRef.current = {
            images: images.map((i) => i.preview),
            video: video?.preview || null,
        };
    }, [images, video]);

    useEffect(() => {
        return () => {
            previewsRef.current.images.forEach((url) => URL.revokeObjectURL(url));
            if (previewsRef.current.video) {
                URL.revokeObjectURL(previewsRef.current.video);
            }
        };
    }, []);

    /* ---------------- Images ---------------- */

    const handleImagesChange = (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length) return;

        const valid = files.filter((f) => IMAGE_TYPES.includes(f.type));
        if (valid.length !== files.length) {
            showAlert("Only PNG, JPG, JPEG and WEBP images are supported.");
        }
        if (!valid.length) return;

        const slots = MAX_IMAGES - images.length;
        if (slots <= 0) {
            showAlert(`You can upload a maximum of ${MAX_IMAGES} reference images.`);
            return;
        }
        if (valid.length > slots) {
            showAlert(
                `You can upload a maximum of ${MAX_IMAGES} reference images. Only ${slots} more ${slots === 1 ? "image was" : "images were"} added.`
            );
        }

        const added = valid.slice(0, slots).map((file) => ({
            id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
            file,
            preview: URL.createObjectURL(file),
        }));

        setImages((prev) => [...prev, ...added]);
        setImageUrls([]);
        setErrorMessage("");
    };

    const handleRemoveImage = (id, e) => {
        e.stopPropagation();
        setImages((prev) => {
            const target = prev.find((i) => i.id === id);
            if (target) URL.revokeObjectURL(target.preview);
            return prev.filter((i) => i.id !== id);
        });
        setImageUrls([]);
    };

    /* ---------------- Video ---------------- */

    const handleVideoChange = (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length) return;

        if (files.length > 1) {
            showAlert("Only one reference video is allowed. The first video was used.");
        }

        const file = files[0];
        if (!VIDEO_TYPES.includes(file.type)) {
            showAlert("Only MP4 and WEBM videos are supported.");
            return;
        }

        if (video?.preview) URL.revokeObjectURL(video.preview);
        setVideo({ file, preview: URL.createObjectURL(file) });
        setVideoUrl("");
        setErrorMessage("");
    };

    const handleRemoveVideo = (e) => {
        e.stopPropagation();
        if (video?.preview) URL.revokeObjectURL(video.preview);
        setVideo(null);
        setVideoUrl("");
    };

    const handleDrop = (e, kind) => {
        e.preventDefault();
        e.stopPropagation();
        const files = e.dataTransfer?.files;
        if (!files?.length) return;
        if (kind === "image") handleImagesChange({ target: { files, value: "" } });
        else handleVideoChange({ target: { files, value: "" } });
    };

    const preventDefault = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    /* ---------------- Validation ---------------- */

    const validate = () => {
        const trimmed = prompt.trim();

        if (!trimmed) return "Please enter a prompt describing how the Multi Assets Video should be made.";
        // if (images.length === 0) return "Please upload at least 1 reference image.";
        if (images.length > MAX_IMAGES) return `You can use a maximum of ${MAX_IMAGES} reference images.`;
        if (!video) return "Please upload a reference video.";

        const durationNumber = Number(duration);
        if (!Number.isFinite(durationNumber) || durationNumber <= 0) {
            return "Please enter a valid duration in seconds.";
        }

        // const imageRefs = [...trimmed.matchAll(/@Image(\d+)/gi)].map((m) => Number(m[1]));
        // const badImage = imageRefs.find((n) => n < 1 || n > images.length);
        // if (badImage) {
        //     return `Your prompt mentions @Image${badImage}, but you uploaded ${images.length} image${images.length === 1 ? "" : "s"}.`;
        // }


        const imageRefs = [...trimmed.matchAll(/@Image(\d+)/gi)].map((m) => Number(m[1]));

        if (images.length === 0 && imageRefs.length > 0) {
            return "Your prompt references images, but no reference images were uploaded. Please upload the images or remove the @Image references.";
        }

        const badImage = imageRefs.find((n) => n < 1 || n > images.length);

        if (badImage) {
            return `Your prompt mentions @Image${badImage}, but you uploaded ${images.length} image${images.length === 1 ? "" : "s"}.`;
        }

        const videoRefs = [...trimmed.matchAll(/@Video(\d+)/gi)].map((m) => Number(m[1]));
        if (videoRefs.some((n) => n !== 1)) {
            return "Only @Video1 can be used in the prompt.";
        }

        if (AIuser && AIuser.credits !== undefined && Number(AIuser.credits) <= 0) {
            return "NO_CREDITS";
        }

        return "";
    };

    /* ---------------- Generate ---------------- */

    const handleGenerate = async () => {
        if (submittingRef.current || isLoading) return;

        setErrorMessage("");
        const problem = validate();

        if (problem === "NO_CREDITS") {
            swal({
                text: "You don't have enough credits! Please buy credits to continue.",
                icon: "error",
                buttons: "Buy",
                className: "modal_class_success",
            }).then((v) => {
                if (v) navigate("/purchase");
            });
            return;
        }

        if (problem) {
            setErrorMessage(problem);
            swal({
                text: problem || "AI Video generation failed.",
                icon: "error",
                buttons: "Ok",
                className: "modal_class_success",
            });
            return;
        }

        submittingRef.current = true;
        setIsLoading(true);
        setResult(null);
        setCopied(false);

        try {
            setLoadingStep("Generating your Multi Assets Video...");

            // Raw files are sent directly as multipart/form-data
            const formData = new FormData();
            formData.append("prompt", prompt.trim());
            images.forEach((img) => formData.append(IMAGE_FIELD_NAME, img.file)); // keeps upload order
            formData.append(VIDEO_FIELD_NAME, video.file);
            formData.append("duration", String(Number(duration)));
            formData.append("resolution", resolution);
            formData.append("aspectRatio", aspectRatio);
            formData.append("wait", "true");

            // Do NOT set Content-Type: the browser adds the multipart boundary
            const response = await fetch(GENERATE_ENDPOINT, {
                method: "POST",
                headers: authHeader(),
                body: formData,
            });

            const data = await response.json().catch(() => null);

            if (!response.ok || !data || data.success !== true) {
                throw new Error(data?.message || data?.error || "Failed to generate the Multi Assets Video.");
            }

            const finalUrl = data.videoUrl || data.outputUrl;
            if (!finalUrl) {
                throw new Error("The video was generated but no playable URL was returned.");
            }

            setResult({
                transactionId: data.transactionId || "",
                cost: data.cost || (data.costUsd != null ? `$${Number(data.costUsd).toFixed(2)}` : ""),
                videoUrl: finalUrl,
                model: data.model || "",
                duration: data.duration ?? Number(duration),
                resolution: data.resolution || resolution,
                aspectRatio: data.aspectRatio || aspectRatio,
                status: data.status || "completed",
            });

            setUserRefetch((prev) => !prev);
        } catch (error) {
            console.error("MOTIONMORPH AI error:", error?.message || error);
            setErrorMessage(
                error?.message === "Failed to fetch"
                    ? "Network error. Please check your connection and try again."
                    : error?.message || "Something went wrong. Please try again."
            );
            swal({
                text: error?.message === "Failed to fetch"
                    ? "Network error. Please check your connection and try again."
                    : error?.message || "Something went wrong. Please try again.",
                icon: "error",
                buttons: "Ok",
                className: "modal_class_success",
            });
        } finally {
            submittingRef.current = false;
            setIsLoading(false);
            setLoadingStep("");
        }
    };

    const handleCopyLink = async () => {
        if (!result?.videoUrl) return;
        try {
            await navigator.clipboard.writeText(result.videoUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            showAlert("Could not copy the link. Please copy it manually.");
        }
    };

    const handleClear = () => {
        images.forEach((i) => URL.revokeObjectURL(i.preview));
        if (video?.preview) URL.revokeObjectURL(video.preview);
        setImages([]);
        setImageUrls([]);
        setVideo(null);
        setVideoUrl("");
        setPrompt("");
        setDuration("10");
        setResolution("1080p");
        setAspectRatio("16:9");
        setErrorMessage("");
        setResult(null);
    };

    // const isFormReady = prompt.trim() && images.length > 0 && video && duration;
    const isFormReady = prompt.trim() && video && duration;

    /* =========================================================
       RENDER
       ========================================================= */

    return (
        <Fragment>
            <div className="dance-video-ai-page">
                {/* Header */}
                <div className="dance-video-ai-top">
                    <p className="dance-video-ai-page-title">MOTIONMORPH AI</p>

                    {/* <div className="dance-video-ai-credits-row">
                        <button type="button" className="dance-video-ai-credits-pill">
                            CREDITS : {credits}
                        </button>
                        <button
                            type="button"
                            className="dance-video-ai-buy-pill"
                            onClick={() => navigate("/purchase")}
                        >
                            BUY CREDITS
                        </button>
                        <button
                            type="button"
                            className="dance-video-ai-icon-pill"
                            onClick={() => navigate("/usage")}
                            aria-label="View usage"
                        >
                            <img src={moneyExchangeIcon} alt="" />
                        </button>
                    </div> */}

                    <div className="credits-actions">


                        <button className="credits-button">
                            CREDITS : {credits}
                        </button>

                        <button
                            className="buy-credits-button"
                            onClick={() => navigate("/purchase")}
                        >
                            BUY CREDITS
                        </button>

                        <button
                            onClick={() => navigate("/usage")}
                            className="money-exchange-button"
                        >
                            <img src={moneyExchangeIcon} alt="usage" />
                        </button>

                        <button
                            type="button"
                            className="money-exchange-button"
                            onClick={() => {
                                // setShowPdfModal(true);
                            }}

                        >
                            <img src={sampleIcon} alt="See sample video" />
                        </button>

                    </div>
                </div>

                {/* Hero */}
                <section className="dance-video-ai-hero">
                    <h1 className="dance-video-ai-hero-title">
                        Transform Any Video.{" "}
                        <span className="dance-video-ai-gradient-text">Reimagine Every Character.</span>
                    </h1>
                    <p className="dance-video-ai-hero-sub">
                        Upload a motion-capture or reference video, replace up to 3 characters with your chosen characters, and preserve the original movement, camera angles, and scene composition.
                    </p>
                </section>

                <div className="dance-video-ai-container">
                    <div className="dance-video-ai-section-head">
                        <h2 className="dance-video-ai-section-title">
                            Swap Up to  <span className="dance-video-ai-gradient-text">3 Characters</span>
                        </h2>
                        <p className="dance-video-ai-section-sub">
                            Upload your video and character reference images, describe the changes, then generate your transformed video.
                        </p>
                    </div>

                    <div className="dance-video-ai-layout">
                        <div className="dance-video-ai-shell">
                            {/* Step 1 — Images */}
                            <div className="dance-video-ai-step">
                                <div className="dance-video-ai-step-header">
                                    <span className="dance-video-ai-step-badge">1</span>
                                    <span className="dance-video-ai-step-title">Upload Reference Images</span>
                                </div>

                                <label className="dance-video-ai-label">
                                    Character Images 
                                    {/* <span className="dance-video-ai-required">*</span> */}

                                    <span className="dance-video-ai-label-note">
                                        {" "}(up to {MAX_IMAGES} images)
                                    </span>
                                </label>

                                <input
                                    ref={imageInputRef}
                                    id="dance-video-ai-image-input"
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    multiple
                                    onChange={handleImagesChange}
                                    className="dance-video-ai-hidden-input"
                                />

                                {images.length === 0 ? (
                                    <label
                                        htmlFor="dance-video-ai-image-input"
                                        className="dance-video-ai-upload"
                                        onDragOver={preventDefault}
                                        onDrop={(e) => handleDrop(e, "image")}
                                    >
                                        <div className="dance-video-ai-upload-orb">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="25" height="25">
                                                <rect x="3" y="3" width="18" height="18" rx="3" />
                                                <circle cx="8.5" cy="8.5" r="1.5" />
                                                <path d="m21 15-5-5L5 21" />
                                            </svg>
                                        </div>
                                        <div className="dance-video-ai-upload-main">
                                            <span>Drop your images here</span>
                                            <span className="dance-video-ai-upload-browse">Browse</span>
                                        </div>
                                        <div className="dance-video-ai-upload-sub">
                                            Add clear photos of the people who should appear in the video
                                        </div>
                                        <div className="dance-video-ai-upload-meta">
                                            <span>JPG</span>
                                            <span>PNG</span>
                                            <span>WEBP</span>
                                        </div>
                                    </label>
                                ) : (
                                    <div className="dance-video-ai-image-grid">
                                        {images.map((img, index) => (
                                            <div className="dance-video-ai-image-card" key={img.id}>
                                                <img src={img.preview} alt={`Reference ${index + 1}`} />
                                                <span className="dance-video-ai-image-number">@Image{index + 1}</span>
                                                <button
                                                    type="button"
                                                    className="dance-video-ai-remove"
                                                    onClick={(e) => handleRemoveImage(img.id, e)}
                                                    aria-label={`Remove image ${index + 1}`}
                                                    disabled={isLoading}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}

                                        {images.length < MAX_IMAGES && (
                                            <label
                                                htmlFor="dance-video-ai-image-input"
                                                className="dance-video-ai-image-add"
                                                onDragOver={preventDefault}
                                                onDrop={(e) => handleDrop(e, "image")}
                                            >
                                                <span className="dance-video-ai-image-add-plus">+</span>
                                                <span>Add image</span>
                                            </label>
                                        )}
                                    </div>
                                )}

                                <div className="dance-video-ai-count">
                                    {images.length}/{MAX_IMAGES} images uploaded
                                </div>
                            </div>

                            {/* Step 2 — Video */}
                            <div className="dance-video-ai-step">
                                <div className="dance-video-ai-step-header">
                                    <span className="dance-video-ai-step-badge">2</span>
                                    <span className="dance-video-ai-step-title">Upload motion caption video</span>
                                </div>

                                <label className="dance-video-ai-label">
                                    Driving Video <span className="dance-video-ai-required">*</span>
                                    <span className="dance-video-ai-label-note"> (1 video, MP4 or WEBM)</span>
                                </label>

                                <input
                                    ref={videoInputRef}
                                    id="dance-video-ai-video-input"
                                    type="file"
                                    accept="video/mp4,video/webm"
                                    onChange={handleVideoChange}
                                    className="dance-video-ai-hidden-input"
                                />

                                {!video ? (
                                    <label
                                        htmlFor="dance-video-ai-video-input"
                                        className="dance-video-ai-upload"
                                        onDragOver={preventDefault}
                                        onDrop={(e) => handleDrop(e, "video")}
                                    >
                                        <div className="dance-video-ai-upload-orb">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="25" height="25">
                                                <rect x="3" y="5" width="13" height="14" rx="2.5" />
                                                <path d="m16 10 5-3v10l-5-3" />
                                            </svg>
                                        </div>
                                        <div className="dance-video-ai-upload-main">
                                            <span>Drop your video here</span>
                                            <span className="dance-video-ai-upload-browse">Browse</span>
                                        </div>
                                        <div className="dance-video-ai-upload-sub">
                                            The people in this video will be replaced with your characters
                                        </div>
                                        <div className="dance-video-ai-upload-meta">
                                            <span>MP4</span>
                                            <span>WEBM</span>
                                        </div>
                                    </label>
                                ) : (
                                    <div className="dance-video-ai-video-card">
                                        <video src={video.preview} controls playsInline preload="metadata" />
                                        <div className="dance-video-ai-video-bar">
                                            <span className="dance-video-ai-video-name" title={video.file.name}>
                                                @Video1 · {video.file.name}
                                            </span>
                                            <span className="dance-video-ai-video-actions">
                                                <label htmlFor="dance-video-ai-video-input" className="dance-video-ai-link-btn">
                                                    Replace
                                                </label>
                                                <button
                                                    type="button"
                                                    className="dance-video-ai-link-btn"
                                                    onClick={handleRemoveVideo}
                                                    disabled={isLoading}
                                                >
                                                    Remove
                                                </button>
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Step 3 — Prompt */}
                            <div className="dance-video-ai-step">
                                <div className="dance-video-ai-step-header">
                                    <span className="dance-video-ai-step-badge">3</span>
                                    <span className="dance-video-ai-step-title">Enter Your Prompt</span>
                                </div>

                                <div className="dance-video-ai-label-row">
                                    <label className="dance-video-ai-label" htmlFor="dance-video-ai-prompt">
                                        Enter Prompt <span className="dance-video-ai-required">*</span>
                                    </label>
                                    <button
                                        type="button"
                                        className="dance-video-ai-clear"
                                        onClick={handleClear}
                                        disabled={isLoading}
                                    >
                                        Clear
                                    </button>
                                </div>

                                <div className="dance-video-ai-prompt-wrap">
                                    <textarea
                                        id="dance-video-ai-prompt"
                                        className="dance-video-ai-textarea"
                                        value={prompt}
                                        maxLength={2000}
                                        onChange={(e) => setPrompt(e.target.value)}
                                        placeholder="Example: @Image1 is Person 1, @Image2 is Person 2. In @Video1, replace Person 1 with @Image1 and Person 2 with @Image2."
                                    />
                                    <span className="dance-video-ai-prompt-count">{prompt.length}/2000</span>
                                </div>

                                <p className="dance-video-ai-hint">
                                    <span className="dance-video-ai-hint-label">How to reference files:</span> use{" "}
                                    <code>@Image1</code>, <code>@Image2</code>, <code>@Image3</code> for images in
                                    upload order and <code>@Video1</code> for the video.{" "}
                                    <button
                                        type="button"
                                        className="dance-video-ai-example-btn"
                                        onClick={() => setPrompt(DEFAULT_PROMPT_EXAMPLE)}
                                        disabled={isLoading}
                                    >
                                        Use example
                                    </button>
                                </p>
                            </div>

                            {/* Step 4 — Settings */}
                            <div className="dance-video-ai-step">
                                <div className="dance-video-ai-step-header">
                                    <span className="dance-video-ai-step-badge">4</span>
                                    <span className="dance-video-ai-step-title">Configure Video Settings</span>
                                </div>

                                <div className="dance-video-ai-settings">
                                    <div className="dance-video-ai-setting-card">
                                        <label className="dance-video-ai-label" htmlFor="dance-video-ai-duration">
                                            Duration (Seconds) <span className="dance-video-ai-required">*</span>
                                        </label>
                                        <input
                                            id="dance-video-ai-duration"
                                            type="number"
                                            min="1"
                                            step="1"
                                            inputMode="numeric"
                                            className="dance-video-ai-number"
                                            value={duration}
                                            onChange={(e) => setDuration(e.target.value)}
                                        />
                                    </div>

                                    <div className="dance-video-ai-setting-card">
                                        <label className="dance-video-ai-label">
                                            Resolution <span className="dance-video-ai-required">*</span>
                                        </label>
                                        <div className="dance-video-ai-chips">
                                            {RESOLUTIONS.map((r) => (
                                                <button
                                                    type="button"
                                                    key={r.value}
                                                    className={`dance-video-ai-chip ${resolution === r.value ? "is-selected" : ""}`}
                                                    onClick={() => setResolution(r.value)}
                                                >
                                                    <span className="dance-video-ai-chip-top">{r.tag}</span>
                                                    <span className="dance-video-ai-chip-bottom">{r.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="dance-video-ai-setting-card">
                                        <label className="dance-video-ai-label">
                                            Aspect Ratio <span className="dance-video-ai-required">*</span>
                                        </label>
                                        <div className="dance-video-ai-chips">
                                            {ASPECTS.map((a) => (
                                                <button
                                                    type="button"
                                                    key={a.value}
                                                    className={`dance-video-ai-chip ${aspectRatio === a.value ? "is-selected" : ""}`}
                                                    onClick={() => setAspectRatio(a.value)}
                                                >
                                                    <span className="dance-video-ai-chip-top">{a.value}</span>
                                                    <span className="dance-video-ai-chip-bottom">{a.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Generate bar */}
                            <div className="dance-video-ai-generate-bar">
                                <div className="dance-video-ai-generate-info">
                                    <span className="dance-video-ai-generate-label">Estimated Generation Cost</span>
                                    <span className="dance-video-ai-generate-value">
                                        {result?.cost || "Shown after generation"}
                                    </span>
                                </div>

                                <div className="dance-video-ai-generate-actions">
                                    <button
                                        type="button"
                                        className="dance-video-ai-clear"
                                        onClick={handleClear}
                                        disabled={isLoading}
                                    >
                                        Clear
                                    </button>
                                    <button
                                        type="button"
                                        className="dance-video-ai-generate-btn"
                                        onClick={handleGenerate}
                                        disabled={!isFormReady || isLoading}
                                    >
                                        {isLoading ? (
                                            <span className="dance-video-ai-spinner" aria-hidden="true" />
                                        ) : (
                                            <AutoAwesomeIcon fontSize="small" />
                                        )}
                                        {isLoading ? "Processing..." : "Generate Video"}
                                    </button>
                                </div>
                            </div>

                            <p className="dance-video-ai-disclaimer">
                                AI-generated content may occasionally contain mistakes. Please verify important
                                information.
                            </p>

                            {/* Loading */}
                            {isLoading && (
                                <div className="dance-video-ai-progress" role="status">
                                    <div className="dance-video-ai-progress-orb">
                                        <AutoAwesomeIcon />
                                    </div>
                                    <p className="dance-video-ai-progress-title">
                                        {loadingStep || "Generating your Multi Assets Video..."}
                                    </p>
                                    <p className="dance-video-ai-progress-text">
                                        This can take a few minutes. Please keep this page open until your
                                        video is ready.
                                    </p>
                                </div>
                            )}

                            {/* Result */}
                            {!isLoading && result && (
                                <div className="dance-video-ai-result">
                                    <div className="dance-video-ai-result-badge">
                                        <span /> Generation Complete
                                    </div>
                                    <h4 className="dance-video-ai-result-title">Your video is ready</h4>

                                    <div className="dance-video-ai-result-player">
                                        <video src={result.videoUrl} controls autoPlay muted playsInline />
                                    </div>

                                    <div className="dance-video-ai-result-grid">
                                        <div className="dance-video-ai-result-item">
                                            <span>Duration</span>
                                            <strong>{result.duration ? `${result.duration}s` : "—"}</strong>
                                        </div>
                                        <div className="dance-video-ai-result-item">
                                            <span>Resolution</span>
                                            <strong>{result.resolution || "—"}</strong>
                                        </div>
                                        <div className="dance-video-ai-result-item">
                                            <span>Aspect Ratio</span>
                                            <strong>{result.aspectRatio || "—"}</strong>
                                        </div>
                                        <div className="dance-video-ai-result-item">
                                            <span>Cost</span>
                                            <strong>{result.cost || "—"}</strong>
                                        </div>
                                        <div className="dance-video-ai-result-item">
                                            <span>Status</span>
                                            <strong>{result.status}</strong>
                                        </div>
                                    </div>

                                    {result.transactionId && (
                                        <p className="dance-video-ai-txid">
                                            Transaction ID: <code>{result.transactionId}</code>
                                        </p>
                                    )}

                                    <div className="dance-video-ai-link-row">
                                        <input
                                            readOnly
                                            value={result.videoUrl}
                                            className="dance-video-ai-link-input"
                                            onFocus={(e) => e.target.select()}
                                            aria-label="Generated video link"
                                        />
                                        <button type="button" className="dance-video-ai-copy-btn" onClick={handleCopyLink}>
                                            <ContentCopyIcon fontSize="small" />
                                            {copied ? "Copied" : "Copy link"}
                                        </button>
                                        <a
                                            className="dance-video-ai-open-btn"
                                            href={result.videoUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Open
                                        </a>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
}
